import { Vector3, Interpolator, base64ToUint8Array, uint8ArrayToBase64, AABB, Matrix4x4, Quaternion, DRef, InterpolatorScalar } from '@zephyr3d/base';
import { AnimationClip } from '../../../animation/animation.js';
import '../../../animation/animationset.js';
import { AnimationTrack } from '../../../animation/animationtrack.js';
import { NodeTranslationTrack } from '../../../animation/translationtrack.js';
import { NodeRotationTrack } from '../../../animation/rotationtrack.js';
import { NodeEulerRotationTrack } from '../../../animation/eulerrotationtrack.js';
import { NodeScaleTrack } from '../../../animation/scaletrack.js';
import { PropertyTrack } from '../../../animation/proptrack.js';
import { MorphTargetTrack } from '../../../animation/morphtrack.js';
import { FixedGeometryCacheTrack } from '../../../animation/fixed_geometry_cache_track.js';
import { BoundingBox } from '../../bounding_volume.js';
import { PCAGeometryCacheTrack } from '../../../animation/pca_geometry_cache_track.js';
import { Skeleton, SkeletonRig, SkinBinding } from '../../../animation/skeleton.js';
import '@zephyr3d/device';
import '../../../animation/joint_dynamics/collision.js';
import '../../../animation/joint_dynamics/solver.js';
import { createTransformAccess, JointDynamicsSystem } from '../../../animation/joint_dynamics/joint_dynamics_system.js';
import { JointDynamicsModifier } from '../../../animation/joint_dynamics_modifier.js';
import { defineProps } from '../types.js';
import '../../../scene/scene.js';
import '../../../values.js';
import '../../../render/render_queue.js';
import '../../../render/primitive.js';
import '../../../material/shader/helper.js';
import '../../../material/lambert.js';
import '../../../material/blinn.js';
import '../../../material/unlit.js';
import '../../../material/particle.js';
import '../../../material/subsurfaceprofile.js';
import '../../../material/meshmaterial.js';
import '../../../material/grassmaterial.js';
import '../../../material/terrain-cm.js';
import '../../../material/pbrmr.js';
import '../../../material/pbrsg.js';
import '../../../material/mtoon.js';
import '../../../material/pbrblueprint.js';
import '../../../material/sprite.js';
import '../../../material/sprite_std.js';
import '../../blueprint/material/texture.js';
import '../../../render/rendergraph/forward_plus_builder.js';
import '../../../render/lightpass.js';
import '../../../render/sky.js';
import '../../../render/clipmap.js';
import '../../../shaders/atmosphere.js';
import { SceneNode } from '../../../scene/scene_node.js';
import '../../../shadow/shadowmapper.js';
import '../../../scene/mesh.js';
import '../../../scene/basesprite.js';
import '../../../scene/meshdrawable.js';
import '../../../material/msdf_text.js';
import '../../../scene/water.js';
import '../../../scene/particlesys.js';
import '../../../scene/batchgroup.js';
import '../../../scene/terrain-cm/terrain-cm.js';
import '../../../scene/terrain-cm/grass.js';
import '../../../scene/raycast_visitor.js';

const jointDynamicsModifierSkeletons = new WeakMap();
function encodeSkeletonBindPose(bindPose) {
    return uint8ArrayToBase64(new Uint8Array(new Float32Array([
        ...bindPose.position,
        ...bindPose.rotation,
        ...bindPose.scale
    ]).buffer));
}
function decodeSkeletonBindPose(encoded) {
    const data = new Float32Array(base64ToUint8Array(encoded).buffer);
    return {
        position: new Vector3(data[0], data[1], data[2]),
        rotation: new Quaternion(data[3], data[4], data[5], data[6]),
        scale: new Vector3(data[7], data[8], data[9])
    };
}
function vectorToArray(value) {
    return [
        value.x,
        value.y,
        value.z
    ];
}
function vectorFromArray(value, defaultValue = Vector3.zero()) {
    return Array.isArray(value) ? new Vector3(value[0] ?? 0, value[1] ?? 0, value[2] ?? 0) : defaultValue.clone();
}
function serializeScalarCurve(value) {
    return {
        mode: value.mode,
        inputs: [
            ...value.inputs
        ],
        outputs: [
            ...value.outputs
        ]
    };
}
function deserializeScalarCurve(value) {
    return new InterpolatorScalar(value?.mode ?? 'step', new Float32Array(value?.inputs ?? [
        0
    ]), new Float32Array(value?.outputs ?? [
        0
    ]));
}
function serializeControllerConfig(value) {
    const curves = {};
    for (const [key, curve] of Object.entries(value.curves)){
        curves[key] = serializeScalarCurve(curve);
    }
    return {
        gravity: vectorToArray(value.gravity),
        windForce: vectorToArray(value.windForce),
        relaxation: value.relaxation,
        subSteps: value.subSteps,
        rootSlideLimit: value.rootSlideLimit,
        rootRotateLimit: value.rootRotateLimit,
        constraintShrinkLimit: value.constraintShrinkLimit,
        blendRatio: value.blendRatio,
        stabilizationFrameRate: value.stabilizationFrameRate,
        isFakeWave: value.isFakeWave,
        fakeWaveSpeed: value.fakeWaveSpeed,
        fakeWavePower: value.fakeWavePower,
        enableSurfaceCollision: value.enableSurfaceCollision,
        enableBroadPhase: value.enableBroadPhase,
        preserveTwist: value.preserveTwist,
        angleLimitConfig: {
            ...value.angleLimitConfig
        },
        curves,
        constraintOptions: {
            ...value.constraintOptions
        }
    };
}
function deserializeControllerConfig(value) {
    if (!value) {
        return undefined;
    }
    const curves = Object.fromEntries(Object.entries(value.curves ?? {}).map(([key, curve])=>[
            key,
            deserializeScalarCurve(curve)
        ]));
    const config = {};
    if (value.gravity) {
        config.gravity = vectorFromArray(value.gravity, new Vector3(0, -9.8, 0));
    }
    if (value.windForce) {
        config.windForce = vectorFromArray(value.windForce);
    }
    for (const key of [
        'relaxation',
        'subSteps',
        'rootSlideLimit',
        'rootRotateLimit',
        'constraintShrinkLimit',
        'blendRatio',
        'stabilizationFrameRate',
        'isFakeWave',
        'fakeWaveSpeed',
        'fakeWavePower',
        'enableSurfaceCollision',
        'enableBroadPhase',
        'preserveTwist'
    ]){
        if (value[key] !== undefined) {
            config[key] = value[key];
        }
    }
    if (value.angleLimitConfig) {
        config.angleLimitConfig = {
            ...value.angleLimitConfig
        };
    }
    if (Object.keys(curves).length > 0) {
        config.curves = curves;
    }
    if (value.constraintOptions) {
        config.constraintOptions = {
            ...value.constraintOptions
        };
    }
    return config;
}
function findSerializedNode(root, id) {
    if (!id) {
        return null;
    }
    const prefabNode = root.getPrefabNode() ?? root;
    return prefabNode.findNodeById(id) ?? root.scene?.findNodeById(id) ?? null;
}
function setJointDynamicsModifierSkeleton(modifier, skeleton) {
    jointDynamicsModifierSkeletons.set(modifier, skeleton);
}
function getJointDynamicsModifierSkeleton(modifier) {
    return jointDynamicsModifierSkeletons.get(modifier) ?? null;
}
const jointDynamicsPropOptions = {
    group: 'Joint Dynamics'
};
function isJointDynamicsEditorPropPersistent() {
    return false;
}
function updateJointDynamicsConfig(modifier, config) {
    modifier.jointDynamicsSystem.controller.updateConfig(config);
}
function createJointDynamicsNumberProp(name, key, label, description, type, minValue) {
    return {
        name,
        description,
        type,
        options: {
            ...jointDynamicsPropOptions,
            label,
            ...minValue !== undefined ? {
                minValue
            } : {}
        },
        isPersistent: isJointDynamicsEditorPropPersistent,
        get (value) {
            value.num[0] = this.jointDynamicsSystem.controller.getConfig()[key];
        },
        set (value) {
            updateJointDynamicsConfig(this, {
                [key]: value.num[0]
            });
        }
    };
}
function createJointDynamicsBooleanProp(name, key, label, description) {
    return {
        name,
        description,
        type: 'bool',
        options: {
            ...jointDynamicsPropOptions,
            label
        },
        isPersistent: isJointDynamicsEditorPropPersistent,
        get (value) {
            value.bool[0] = this.jointDynamicsSystem.controller.getConfig()[key];
        },
        set (value) {
            updateJointDynamicsConfig(this, {
                [key]: value.bool[0]
            });
        }
    };
}
function createJointDynamicsVectorProp(name, key, label, description) {
    return {
        name,
        description,
        type: 'vec3',
        options: {
            ...jointDynamicsPropOptions,
            label
        },
        isPersistent: isJointDynamicsEditorPropPersistent,
        get (value) {
            const v = this.jointDynamicsSystem.controller.getConfig()[key];
            value.num[0] = v.x;
            value.num[1] = v.y;
            value.num[2] = v.z;
        },
        set (value) {
            updateJointDynamicsConfig(this, {
                [key]: new Vector3(value.num[0], value.num[1], value.num[2])
            });
        }
    };
}
function createJointDynamicsAngleLimitNumberProp(name, key, label, description) {
    return {
        name,
        description,
        type: 'float',
        options: {
            ...jointDynamicsPropOptions,
            label
        },
        isPersistent: isJointDynamicsEditorPropPersistent,
        get (value) {
            value.num[0] = this.jointDynamicsSystem.controller.getConfig().angleLimitConfig[key];
        },
        set (value) {
            updateJointDynamicsConfig(this, {
                angleLimitConfig: {
                    [key]: value.num[0]
                }
            });
        }
    };
}
function createJointDynamicsAngleLimitBooleanProp(name, key, label, description) {
    return {
        name,
        description,
        type: 'bool',
        options: {
            ...jointDynamicsPropOptions,
            label
        },
        isPersistent: isJointDynamicsEditorPropPersistent,
        get (value) {
            value.bool[0] = this.jointDynamicsSystem.controller.getConfig().angleLimitConfig[key];
        },
        set (value) {
            updateJointDynamicsConfig(this, {
                angleLimitConfig: {
                    [key]: value.bool[0]
                }
            });
        }
    };
}
function createJointDynamicsConstraintOptionProp(name, key, label, description) {
    return {
        name,
        description,
        type: 'bool',
        options: {
            ...jointDynamicsPropOptions,
            label
        },
        isPersistent: isJointDynamicsEditorPropPersistent,
        get (value) {
            value.bool[0] = this.jointDynamicsSystem.controller.getConfig().constraintOptions[key];
        },
        set (value) {
            updateJointDynamicsConfig(this, {
                constraintOptions: {
                    [key]: value.bool[0]
                }
            });
        }
    };
}
function createJointDynamicsCurveProp(name, key, label, description) {
    return {
        name,
        description,
        type: 'float',
        options: {
            ...jointDynamicsPropOptions,
            label
        },
        isPersistent: isJointDynamicsEditorPropPersistent,
        get (value) {
            value.num[0] = this.jointDynamicsSystem.controller.getConfig().curves[key].evaluate(0);
        },
        set (value) {
            const val = value.num[0];
            updateJointDynamicsConfig(this, {
                curves: {
                    [key]: InterpolatorScalar.constant(val)
                }
            });
        }
    };
/*
  return {
    name,
    description,
    type: 'object',
    readonly: true,
    options: {
      ...jointDynamicsPropOptions,
      label,
      objectTypes: [Interpolator],
      edit: 'curve1f' as const
    },
    isPersistent: isJointDynamicsEditorPropPersistent,
    get(this: JointDynamicsModifier, value: Required<Pick<PropertyValue, 'object'>>) {
      value.object[0] = this.jointDynamicsSystem.controller.getConfig().curves[key];
    },
    set(this: JointDynamicsModifier, value: Required<Pick<PropertyValue, 'object'>>) {
      const curve = value.object[0];
      if (curve instanceof InterpolatorScalar) {
        updateJointDynamicsConfig(this, { curves: { [key]: curve } as Partial<ControllerConfig['curves']> });
      } else if (curve instanceof Interpolator && curve.target === 'number') {
        updateJointDynamicsConfig(this, {
          curves: {
            [key]: new InterpolatorScalar(
              curve.mode,
              new Float32Array(curve.inputs),
              new Float32Array(curve.outputs)
            )
          } as Partial<ControllerConfig['curves']>
        });
      }
    }
  };
  */ }
/** @internal */ function getJointDynamicsModifierClass() {
    return {
        ctor: JointDynamicsModifier,
        name: 'JointDynamicsModifier',
        createFunc (ctx, init) {
            const skeleton = ctx.findSkeletonRigById(init.skeleton) ?? ctx.findSkeletonById(init.skeleton)?.rig ?? null;
            const systemRoot = findSerializedNode(ctx, init.systemRoot);
            if (!skeleton || !systemRoot) {
                return {
                    obj: null,
                    loadProps: false
                };
            }
            const chains = (init.chains ?? []).map((chain)=>{
                const start = findSerializedNode(ctx, chain.start);
                const end = findSerializedNode(ctx, chain.end);
                return start && end ? {
                    start,
                    end
                } : null;
            }).filter((chain)=>{
                return !!chain && chain.start.isParentOf(chain.end);
            });
            if (chains.length === 0) {
                return {
                    obj: null,
                    loadProps: false
                };
            }
            const serializedColliders = init.colliders ?? [];
            const colliders = serializedColliders.map((item)=>{
                const transform = findSerializedNode(ctx, item.transform);
                return {
                    r: {
                        ...item.collider
                    },
                    transform: transform ? createTransformAccess(transform) : createTransformAccess(new SceneNode(null), false)
                };
            });
            const serializedGrabbers = init.grabbers ?? [];
            const grabbers = serializedGrabbers.map((item)=>{
                const transform = findSerializedNode(ctx, item.transform);
                return {
                    r: {
                        ...item.grabber
                    },
                    transform: transform ? createTransformAccess(transform) : createTransformAccess(new SceneNode(null), false),
                    enabled: item.enabled ?? false
                };
            });
            const flatPlanes = (init.flatPlanes ?? []).map((item)=>({
                    up: vectorFromArray(item.up, Vector3.axisPY()),
                    position: vectorFromArray(item.position),
                    enabled: item.enabled ?? true
                }));
            const controllerConfig = deserializeControllerConfig(init.controllerConfig);
            const config = {
                chainConfig: {
                    systemRoot,
                    chains
                },
                controllerConfig
            };
            const system = new JointDynamicsSystem(config, colliders, grabbers, flatPlanes.map((item)=>({
                    up: item.up,
                    position: item.position
                })));
            for(let i = 0; i < serializedColliders.length; i++){
                if (serializedColliders[i]?.enabled === false) {
                    system.controller.setColliderEnabledAt(i, false);
                }
            }
            for(let i = 0; i < flatPlanes.length; i++){
                if (!flatPlanes[i].enabled) {
                    system.controller.setFlatPlaneEnabledAt(i, false);
                }
            }
            const modifier = new JointDynamicsModifier(system);
            modifier.enabled = init.enabled ?? true;
            setJointDynamicsModifierSkeleton(modifier, skeleton);
            return {
                obj: modifier,
                loadProps: false
            };
        },
        getInitParams (obj) {
            const skeleton = getJointDynamicsModifierSkeleton(obj);
            const chainConfig = obj.jointDynamicsSystem.chainConfig;
            const controllerConfig = obj.jointDynamicsSystem.controller.getConfig();
            const init = {
                skeleton: skeleton?.persistentId ?? '',
                systemRoot: chainConfig.systemRoot.persistentId,
                chains: chainConfig.chains.map((chain)=>({
                        start: chain.start.persistentId,
                        end: chain.end.persistentId
                    })),
                controllerConfig: serializeControllerConfig(controllerConfig),
                colliders: obj.jointDynamicsSystem.getColliderSnapshots().map((item)=>({
                        collider: {
                            ...item.r
                        },
                        transform: item.transform instanceof SceneNode ? item.transform.persistentId : undefined,
                        enabled: item.enabled
                    })),
                flatPlanes: obj.jointDynamicsSystem.getFlatPlaneSnapshots().map((item)=>({
                        up: vectorToArray(item.up),
                        position: vectorToArray(item.position),
                        enabled: item.enabled
                    })),
                grabbers: obj.jointDynamicsSystem.getGrabberSnapshots().map((item)=>({
                        grabber: {
                            ...item.r
                        },
                        transform: item.transform instanceof SceneNode ? item.transform.persistentId : undefined,
                        enabled: item.enabled
                    })),
                enabled: obj.enabled
            };
            return init;
        },
        getProps () {
            return defineProps([
                {
                    name: 'Commands',
                    description: 'Reset joint dynamics modifier state',
                    type: 'command',
                    command () {
                        this.reset();
                        return false;
                    },
                    get (value) {
                        value.str[0] = 'Reset';
                    }
                },
                {
                    name: 'Enabled',
                    description: 'Whether this joint dynamics modifier is active',
                    type: 'bool',
                    options: {
                        ...jointDynamicsPropOptions,
                        label: 'Enabled'
                    },
                    isPersistent: isJointDynamicsEditorPropPersistent,
                    get (value) {
                        value.bool[0] = this.enabled;
                    },
                    set (value) {
                        this.enabled = value.bool[0];
                    }
                },
                {
                    name: 'Weight',
                    description: 'Physics blend weight. 0 uses animation only, 1 uses full joint dynamics',
                    type: 'float',
                    options: {
                        ...jointDynamicsPropOptions,
                        label: 'Weight',
                        minValue: 0,
                        maxValue: 1
                    },
                    isPersistent: isJointDynamicsEditorPropPersistent,
                    get (value) {
                        value.num[0] = this.weight;
                    },
                    set (value) {
                        this.weight = value.num[0];
                    }
                },
                createJointDynamicsVectorProp('Gravity', 'gravity', 'Gravity', 'Global gravity vector'),
                createJointDynamicsVectorProp('WindForce', 'windForce', 'Wind Force', 'Global wind force vector'),
                createJointDynamicsNumberProp('Relaxation', 'relaxation', 'Relaxation', 'Constraint relaxation iterations per substep', 'int', 0),
                createJointDynamicsNumberProp('SubSteps', 'subSteps', 'Sub Steps', 'Simulation substeps per frame', 'int', 1),
                createJointDynamicsNumberProp('RootSlideLimit', 'rootSlideLimit', 'Root Slide Limit', 'Maximum system-local root movement left as relative physics lag after large root motion. -1 means unlimited', 'float'),
                createJointDynamicsNumberProp('RootRotateLimit', 'rootRotateLimit', 'Root Rotate Limit', 'Maximum root rotation angle per substep. -1 means unlimited', 'float'),
                createJointDynamicsNumberProp('ConstraintShrinkLimit', 'constraintShrinkLimit', 'Shrink Limit', 'Upper limit for horizontal and shear constraint shrink power', 'float', 0),
                createJointDynamicsNumberProp('StabilizationFrameRate', 'stabilizationFrameRate', 'Stabilization FPS', 'Target frame rate used by stabilization logic', 'float', 0),
                createJointDynamicsBooleanProp('FakeWave', 'isFakeWave', 'Fake Wave', 'Enable sinusoidal fake wave on leaf bones'),
                createJointDynamicsNumberProp('FakeWaveSpeed', 'fakeWaveSpeed', 'Fake Wave Speed', 'Global fake wave speed', 'float'),
                createJointDynamicsNumberProp('FakeWavePower', 'fakeWavePower', 'Fake Wave Power', 'Global fake wave amplitude multiplier', 'float'),
                createJointDynamicsBooleanProp('EnableSurfaceCollision', 'enableSurfaceCollision', 'Surface Collision', 'Enable triangle-based surface collision'),
                createJointDynamicsBooleanProp('EnableBroadPhase', 'enableBroadPhase', 'Broad Phase', 'Enable broad-phase pruning before precise collider tests'),
                createJointDynamicsBooleanProp('PreserveTwist', 'preserveTwist', 'Preserve Twist', 'Preserve each joint initial local twist after physics simulation'),
                createJointDynamicsAngleLimitNumberProp('AngleLimit', 'angleLimit', 'Angle Limit', 'Post-simulation parent-child angle limit. -1 disables angle limiting'),
                createJointDynamicsAngleLimitBooleanProp('AngleLimitFromRoot', 'limitFromRoot', 'Limit From Root', 'Apply angle limiting from the chain root'),
                createJointDynamicsConstraintOptionProp('StructuralVertical', 'structuralVertical', 'Structural Vertical', 'Generate parent-child vertical constraints'),
                createJointDynamicsConstraintOptionProp('StructuralHorizontal', 'structuralHorizontal', 'Structural Horizontal', 'Generate same-depth horizontal constraints across chains'),
                createJointDynamicsConstraintOptionProp('Shear', 'shear', 'Shear', 'Generate diagonal cross-bracing constraints'),
                createJointDynamicsConstraintOptionProp('BendingVertical', 'bendingVertical', 'Bending Vertical', 'Generate skip-one vertical bending constraints'),
                createJointDynamicsConstraintOptionProp('BendingHorizontal', 'bendingHorizontal', 'Bending Horizontal', 'Generate skip-one horizontal bending constraints'),
                createJointDynamicsConstraintOptionProp('Loop', 'isLoop', 'Loop', 'Connect the last root point back to the first root point'),
                createJointDynamicsConstraintOptionProp('CollideStructuralVertical', 'collideStructuralVertical', 'Collide Structural V', 'Enable per-constraint collision on structural vertical pairs'),
                createJointDynamicsConstraintOptionProp('CollideStructuralHorizontal', 'collideStructuralHorizontal', 'Collide Structural H', 'Enable per-constraint collision on structural horizontal pairs'),
                createJointDynamicsConstraintOptionProp('CollideShear', 'collideShear', 'Collide Shear', 'Enable per-constraint collision on shear pairs'),
                createJointDynamicsConstraintOptionProp('ConstraintSurfaceCollision', 'enableSurfaceCollision', 'Constraint Surface Collision', 'Enable surface collision in constraint generation'),
                createJointDynamicsCurveProp('MassScale', 'massScale', 'Mass Scale', 'Depth curve that scales point mass'),
                createJointDynamicsCurveProp('GravityScale', 'gravityScale', 'Gravity Scale', 'Depth curve that scales gravity strength'),
                createJointDynamicsCurveProp('WindForceScale', 'windForceScale', 'Wind Force Scale', 'Depth curve that scales wind force'),
                createJointDynamicsCurveProp('Resistance', 'resistance', 'Resistance', 'Depth curve for velocity damping'),
                createJointDynamicsCurveProp('Hardness', 'hardness', 'Hardness', 'Depth curve for restore-to-animation stiffness'),
                createJointDynamicsCurveProp('Friction', 'friction', 'Friction', 'Depth curve for collision friction'),
                createJointDynamicsCurveProp('PointRadius', 'pointRadius', 'Point Radius', 'Depth curve for collision radius'),
                createJointDynamicsCurveProp('SliderJointLength', 'sliderJointLength', 'Slider Joint Length', 'Depth curve for extra horizontal and shear constraint slack'),
                createJointDynamicsCurveProp('AllShrinkScale', 'allShrinkScale', 'All Shrink Scale', 'Depth curve for global shrink stiffness multiplier'),
                createJointDynamicsCurveProp('AllStretchScale', 'allStretchScale', 'All Stretch Scale', 'Depth curve for global stretch stiffness multiplier'),
                createJointDynamicsCurveProp('StructuralShrinkVertical', 'structuralShrinkVertical', 'Structural Shrink V', 'Depth curve for structural vertical shrink stiffness'),
                createJointDynamicsCurveProp('StructuralStretchVertical', 'structuralStretchVertical', 'Structural Stretch V', 'Depth curve for structural vertical stretch stiffness'),
                createJointDynamicsCurveProp('StructuralShrinkHorizontal', 'structuralShrinkHorizontal', 'Structural Shrink H', 'Depth curve for structural horizontal shrink stiffness'),
                createJointDynamicsCurveProp('StructuralStretchHorizontal', 'structuralStretchHorizontal', 'Structural Stretch H', 'Depth curve for structural horizontal stretch stiffness'),
                createJointDynamicsCurveProp('ShearShrink', 'shearShrink', 'Shear Shrink', 'Depth curve for shear shrink stiffness'),
                createJointDynamicsCurveProp('ShearStretch', 'shearStretch', 'Shear Stretch', 'Depth curve for shear stretch stiffness'),
                createJointDynamicsCurveProp('BendingShrinkVertical', 'bendingShrinkVertical', 'Bending Shrink V', 'Depth curve for bending vertical shrink stiffness'),
                createJointDynamicsCurveProp('BendingStretchVertical', 'bendingStretchVertical', 'Bending Stretch V', 'Depth curve for bending vertical stretch stiffness'),
                createJointDynamicsCurveProp('BendingShrinkHorizontal', 'bendingShrinkHorizontal', 'Bending Shrink H', 'Depth curve for bending horizontal shrink stiffness'),
                createJointDynamicsCurveProp('BendingStretchHorizontal', 'bendingStretchHorizontal', 'Bending Stretch H', 'Depth curve for bending horizontal stretch stiffness'),
                createJointDynamicsCurveProp('FakeWavePowerCurve', 'fakeWavePower', 'Fake Wave Power Curve', 'Depth curve for fake wave amplitude'),
                createJointDynamicsCurveProp('FakeWaveFreq', 'fakeWaveFreq', 'Fake Wave Freq', 'Depth curve for fake wave frequency offset')
            ]);
        }
    };
}
/** @internal */ function getInterpolatorClass() {
    return {
        ctor: Interpolator,
        name: 'Interpolator',
        createFunc (ctx, init) {
            const inputs = init.inputs ? new Float32Array(base64ToUint8Array(init.inputs).buffer) : new Float32Array();
            const outputs = init.outputs ? new Float32Array(base64ToUint8Array(init.outputs).buffer) : new Float32Array();
            return {
                obj: new Interpolator(init.mode, init.target, inputs, outputs)
            };
        },
        getInitParams (obj) {
            const inputs = obj.inputs instanceof Float32Array ? obj.inputs : obj.inputs ? new Float32Array(obj.inputs) : new Float32Array();
            const outputs = obj.outputs instanceof Float32Array ? obj.outputs : obj.outputs ? new Float32Array(obj.outputs) : new Float32Array();
            return {
                mode: obj.mode,
                target: obj.target,
                inputs: uint8ArrayToBase64(new Uint8Array(inputs.buffer, inputs.byteOffset, inputs.byteLength)),
                outputs: uint8ArrayToBase64(new Uint8Array(outputs.buffer, outputs.byteOffset, outputs.byteLength))
            };
        },
        getProps () {
            return defineProps([
                {
                    name: 'Mode',
                    description: 'What kind of method does this interpolator use to interpolate data, possible value is `step` | `linear` | `cubicspline` | `cubicspline-natural`',
                    type: 'string',
                    get (value) {
                        value.str[0] = this.mode;
                    }
                },
                {
                    name: 'Target',
                    description: 'What type of data this interpolator handles, possible value is `number` | `vec2` | `vec3` | `vec4 | `quat`',
                    type: 'string',
                    get (value) {
                        value.str[0] = this.target ?? '';
                    }
                }
            ]);
        }
    };
}
/** @internal */ function getMorphTrackClass() {
    return {
        ctor: MorphTargetTrack,
        name: 'MorphTargetTrack',
        getProps () {
            return defineProps([
                {
                    name: 'TrackName',
                    description: 'The track name',
                    type: 'string',
                    options: {
                        label: 'Name'
                    },
                    get (value) {
                        value.str[0] = this.name;
                    },
                    set (value) {
                        this.name = value.str[0];
                    }
                },
                {
                    name: 'Interpolator',
                    description: 'Interpolator object for this track',
                    type: 'object',
                    options: {
                        objectTypes: [
                            Interpolator
                        ]
                    },
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.interpolator;
                    },
                    set (value) {
                        this.interpolator = value.object[0];
                    }
                },
                {
                    name: 'DefaultWeights',
                    description: 'Default weights for this track',
                    type: 'object',
                    get (value) {
                        value.object[0] = this.defaultWeights ?? null;
                    },
                    set (value) {
                        this.defaultWeights = value.object[0] ?? null;
                    }
                },
                {
                    name: 'OriginBoundingBox',
                    description: 'Original bounding box',
                    type: 'object',
                    get (value) {
                        if (!this.originBoundingBox) {
                            value.object[0] = null;
                        } else {
                            const arr = [
                                ...this.originBoundingBox.minPoint,
                                ...this.originBoundingBox.maxPoint
                            ];
                            value.object[0] = arr;
                        }
                    },
                    set (value) {
                        if (!value.object[0]) {
                            this.originBoundingBox = null;
                        } else {
                            const bbox = new AABB();
                            const values = value.object[0];
                            bbox.minPoint.setXYZ(values[0], values[1], values[2]);
                            bbox.maxPoint.setXYZ(values[3], values[4], values[5]);
                            this.originBoundingBox = bbox;
                        }
                    }
                },
                {
                    name: 'BoundingBox',
                    description: 'Bounding box when this track in action',
                    type: 'object',
                    get (value) {
                        if (!this.boundingBox) {
                            value.object[0] = null;
                        } else {
                            const arr = [];
                            for (const box of this.boundingBox){
                                arr.push(...box.minPoint);
                                arr.push(...box.maxPoint);
                            }
                            value.object[0] = arr;
                        }
                    },
                    set (value) {
                        if (!value.object[0]) {
                            this.boundingBox = null;
                        } else {
                            const arr = [];
                            const values = value.object[0];
                            for(let i = 0; i < values.length / 6; i++){
                                arr.push(new BoundingBox(new Vector3(values[i * 6 + 0], values[i * 6 + 1], values[i * 6 + 2]), new Vector3(values[i * 6 + 3], values[i * 6 + 4], values[i * 6 + 5])));
                            }
                            this.boundingBox = arr;
                        }
                    }
                },
                {
                    name: 'TrackTarget',
                    description: 'Target object this track applys to',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.target;
                    },
                    set (value) {
                        this.target = value.str[0];
                    }
                }
            ]);
        }
    };
}
function encodeFloat32Array(values) {
    return uint8ArrayToBase64(new Uint8Array(values.buffer, values.byteOffset, values.byteLength));
}
function decodeFloat32Array(value) {
    return new Float32Array(base64ToUint8Array(value).buffer);
}
function getFixedGeometryCacheTrackClass() {
    return {
        ctor: FixedGeometryCacheTrack,
        name: 'FixedGeometryCacheTrack',
        getProps () {
            return defineProps([
                {
                    name: 'TrackName',
                    description: 'The track name',
                    type: 'string',
                    options: {
                        label: 'Name'
                    },
                    get (value) {
                        value.str[0] = this.name;
                    },
                    set (value) {
                        this.name = value.str[0];
                    }
                },
                {
                    name: 'TrackTarget',
                    description: 'Target object this track applys to',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.target;
                    },
                    set (value) {
                        this.target = value.str[0];
                    }
                },
                {
                    name: 'Times',
                    description: 'Keyframe timestamps for this geometry cache track',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = encodeFloat32Array(this.times);
                    },
                    set (value) {
                        this.times = value.str[0] ? decodeFloat32Array(value.str[0]) : new Float32Array();
                    }
                },
                {
                    name: 'Frames',
                    description: 'Serialized geometry cache frames for this track',
                    type: 'object',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.frames.map((frame)=>({
                                positions: encodeFloat32Array(frame.positions),
                                normals: frame.normals ? encodeFloat32Array(frame.normals) : '',
                                bounds: [
                                    frame.boundingBox.minPoint.x,
                                    frame.boundingBox.minPoint.y,
                                    frame.boundingBox.minPoint.z,
                                    frame.boundingBox.maxPoint.x,
                                    frame.boundingBox.maxPoint.y,
                                    frame.boundingBox.maxPoint.z
                                ]
                            }));
                    },
                    set (value) {
                        this.frames = (value.object[0] ?? []).map((frame)=>({
                                positions: decodeFloat32Array(frame.positions),
                                normals: frame.normals ? decodeFloat32Array(frame.normals) : null,
                                boundingBox: new BoundingBox(new Vector3(frame.bounds[0], frame.bounds[1], frame.bounds[2]), new Vector3(frame.bounds[3], frame.bounds[4], frame.bounds[5]))
                            }));
                    }
                }
            ]);
        }
    };
}
function getPCAGeometryCacheTrackClass() {
    return {
        ctor: PCAGeometryCacheTrack,
        name: 'PCAGeometryCacheTrack',
        getProps () {
            return defineProps([
                {
                    name: 'TrackName',
                    description: 'The track name',
                    type: 'string',
                    options: {
                        label: 'Name'
                    },
                    get (value) {
                        value.str[0] = this.name;
                    },
                    set (value) {
                        this.name = value.str[0];
                    }
                },
                {
                    name: 'TrackTarget',
                    description: 'Target object this track applys to',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.target;
                    },
                    set (value) {
                        this.target = value.str[0];
                    }
                },
                {
                    name: 'Times',
                    description: 'Keyframe timestamps for this PCA geometry cache track',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = encodeFloat32Array(this.times);
                    },
                    set (value) {
                        this.times = value.str[0] ? decodeFloat32Array(value.str[0]) : new Float32Array();
                    }
                },
                {
                    name: 'Bounds',
                    description: 'Per-frame bounding boxes for the PCA geometry cache',
                    type: 'object',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.bounds;
                    },
                    set (value) {
                        this.bounds = value.object[0] ?? [];
                    }
                },
                {
                    name: 'PositionReference',
                    description: 'Reference position data for the PCA geometry cache',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.positionReference ? encodeFloat32Array(this.positionReference) : '';
                    },
                    set (value) {
                        this.positionReference = value.str[0] ? decodeFloat32Array(value.str[0]) : null;
                    }
                },
                {
                    name: 'PositionMean',
                    description: 'Mean position data for the PCA geometry cache',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = encodeFloat32Array(this.positionMean);
                    },
                    set (value) {
                        this.positionMean = value.str[0] ? decodeFloat32Array(value.str[0]) : new Float32Array();
                    }
                },
                {
                    name: 'PositionBases',
                    description: 'Principal position basis vectors for the PCA geometry cache',
                    type: 'object',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.positionBases.map((item)=>encodeFloat32Array(item));
                    },
                    set (value) {
                        this.positionBases = (value.object[0] ?? []).map((item)=>decodeFloat32Array(item));
                    }
                },
                {
                    name: 'PositionCoefficients',
                    description: 'Per-frame position coefficient data for the PCA geometry cache',
                    type: 'object',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.positionCoefficients.map((item)=>encodeFloat32Array(item));
                    },
                    set (value) {
                        this.positionCoefficients = (value.object[0] ?? []).map((item)=>decodeFloat32Array(item));
                    }
                },
                {
                    name: 'NormalMean',
                    description: 'Mean normal data for the PCA geometry cache',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.normalMean ? encodeFloat32Array(this.normalMean) : '';
                    },
                    set (value) {
                        this.normalMean = value.str[0] ? decodeFloat32Array(value.str[0]) : null;
                    }
                },
                {
                    name: 'NormalBases',
                    description: 'Principal normal basis vectors for the PCA geometry cache',
                    type: 'object',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.normalBases?.map((item)=>encodeFloat32Array(item)) ?? [];
                    },
                    set (value) {
                        this.normalBases = (value.object[0] ?? []).map((item)=>decodeFloat32Array(item)) ?? null;
                    }
                },
                {
                    name: 'NormalCoefficients',
                    description: 'Per-frame normal coefficient data for the PCA geometry cache',
                    type: 'object',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.normalCoefficients?.map((item)=>encodeFloat32Array(item)) ?? [];
                    },
                    set (value) {
                        this.normalCoefficients = (value.object[0] ?? []).map((item)=>decodeFloat32Array(item)) ?? null;
                    }
                }
            ]);
        }
    };
}
/** @internal */ function getNodeRotationTrackClass() {
    return {
        ctor: NodeRotationTrack,
        name: 'NodeRotationTrack',
        getProps () {
            return defineProps([
                {
                    name: 'TrackName',
                    description: 'The track name',
                    type: 'string',
                    options: {
                        label: 'Name'
                    },
                    get (value) {
                        value.str[0] = this.name;
                    },
                    set (value) {
                        this.name = value.str[0];
                    }
                },
                {
                    name: 'Interpolator',
                    description: 'Interpolator object for this track',
                    type: 'object',
                    options: {
                        objectTypes: [
                            Interpolator
                        ]
                    },
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.interpolator;
                    },
                    set (value) {
                        this.interpolator = value.object[0];
                    }
                },
                {
                    name: 'TrackTarget',
                    description: 'Target object this track applys to',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.target;
                    },
                    set (value) {
                        this.target = value.str[0];
                    }
                }
            ]);
        }
    };
}
/** @internal */ function getNodeEulerRotationTrackClass() {
    return {
        ctor: NodeEulerRotationTrack,
        name: 'NodeEulerRotationTrack',
        getProps () {
            return defineProps([
                {
                    name: 'TrackName',
                    description: 'The track name',
                    type: 'string',
                    options: {
                        label: 'Name'
                    },
                    get (value) {
                        value.str[0] = this.name;
                    },
                    set (value) {
                        this.name = value.str[0];
                    }
                },
                {
                    name: 'Interpolator',
                    description: 'Interpolator object for this track',
                    type: 'object',
                    options: {
                        objectTypes: [
                            Interpolator
                        ]
                    },
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.interpolator;
                    },
                    set (value) {
                        this.interpolator = value.object[0];
                    }
                },
                {
                    name: 'TrackTarget',
                    description: 'Target object this track applys to',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.target;
                    },
                    set (value) {
                        this.target = value.str[0];
                    }
                }
            ]);
        }
    };
}
/** @internal */ function getNodeTranslationTrackClass() {
    return {
        ctor: NodeTranslationTrack,
        name: 'NodeTranslationTrack',
        getProps () {
            return defineProps([
                {
                    name: 'TrackName',
                    description: 'The track name',
                    type: 'string',
                    options: {
                        label: 'Name'
                    },
                    get (value) {
                        value.str[0] = this.name;
                    },
                    set (value) {
                        this.name = value.str[0];
                    }
                },
                {
                    name: 'Interpolator',
                    description: 'Interpolator object for this track',
                    type: 'object',
                    options: {
                        objectTypes: [
                            Interpolator
                        ]
                    },
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.interpolator;
                    },
                    set (value) {
                        this.interpolator = value.object[0];
                    }
                },
                {
                    name: 'TrackTarget',
                    description: 'Target object this track applys to',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.target;
                    },
                    set (value) {
                        this.target = value.str[0];
                    }
                }
            ]);
        }
    };
}
/** @internal */ function getNodeScaleTrackClass() {
    return {
        ctor: NodeScaleTrack,
        name: 'NodeScaleTrack',
        getProps () {
            return defineProps([
                {
                    name: 'TrackName',
                    description: 'The track name',
                    type: 'string',
                    options: {
                        label: 'Name'
                    },
                    get (value) {
                        value.str[0] = this.name;
                    },
                    set (value) {
                        this.name = value.str[0];
                    }
                },
                {
                    name: 'Interpolator',
                    description: 'Interpolator object for this track',
                    type: 'object',
                    options: {
                        objectTypes: [
                            Interpolator
                        ]
                    },
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = this.interpolator;
                    },
                    set (value) {
                        this.interpolator = value.object[0];
                    }
                },
                {
                    name: 'TrackTarget',
                    description: 'Target object this track applys to',
                    type: 'string',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.str[0] = this.target;
                    },
                    set (value) {
                        this.target = value.str[0];
                    }
                }
            ]);
        }
    };
}
/** @internal */ function getPropTrackClass(manager) {
    return {
        ctor: PropertyTrack,
        name: 'PropertyTrack',
        createFunc (ctx, init) {
            return {
                obj: new PropertyTrack(manager.getPropertyByName(init))
            };
        },
        getInitParams (obj) {
            return manager.getPropertyName(obj.getProp());
        },
        getProps () {
            return defineProps([
                {
                    name: 'TrackName',
                    description: 'The track name',
                    type: 'string',
                    options: {
                        label: 'Name'
                    },
                    get (value) {
                        value.str[0] = this.name;
                    },
                    set (value) {
                        this.name = value.str[0];
                    }
                },
                {
                    name: 'TrackTarget',
                    description: 'Target object this track applys to',
                    type: 'string',
                    options: {
                        label: 'Target'
                    },
                    get (value) {
                        value.str[0] = this.target;
                    },
                    set (value) {
                        this.target = value.str[0];
                    }
                },
                {
                    name: 'TrackProp',
                    description: 'Which property of the target object does this track handle',
                    type: 'string',
                    get (value) {
                        value.str[0] = manager.getPropertyName(this.getProp()) ?? '';
                    }
                },
                {
                    name: 'TrackData',
                    description: 'Interpolator data used by this property track',
                    type: 'object_array',
                    options: {
                        objectTypes: [
                            Interpolator
                        ]
                    },
                    isHidden (index) {
                        return index >= 0;
                    },
                    get (value) {
                        value.object = this.interpolatorAlpha ? [
                            this.interpolator,
                            this.interpolatorAlpha
                        ] : [
                            this.interpolator
                        ];
                    },
                    set (value) {
                        this.interpolator = value.object[0];
                        this.interpolatorAlpha = value.object[1];
                    }
                }
            ]);
        }
    };
}
/** @internal */ function getSkeletonClass() {
    return {
        ctor: Skeleton,
        name: 'Skeleton',
        createFunc (ctx, init) {
            const prefabNode = ctx.getPrefabNode() ?? ctx;
            const joints = init.joints.map((id)=>prefabNode.findNodeById(id)).map((node)=>{
                node.jointTypeT = 'static';
                node.jointTypeS = 'static';
                node.jointTypeR = 'static';
                return node;
            });
            const rootJoint = init.rootJoint ? prefabNode.findNodeById(init.rootJoint) : null;
            const inverseBindMatricesArray = new Float32Array(base64ToUint8Array(init.inverseBindMatrices).buffer);
            const bindPoseArray = new Float32Array(base64ToUint8Array(init.bindPose ?? init.bindPoseMatrices).buffer);
            const rootBindPose = init.rootBindPose ? decodeSkeletonBindPose(init.rootBindPose) : undefined;
            const inverseBindMatrices = [];
            const bindPose = [];
            for(let i = 0; i < joints.length; i++){
                inverseBindMatrices.push(new Matrix4x4(inverseBindMatricesArray.slice(i * 16, i * 16 + 16)));
                if (!init.bindPose) {
                    const matrix = new Matrix4x4(bindPoseArray.slice(i * 16, i * 16 + 16));
                    const trs = {
                        position: new Vector3(),
                        rotation: new Quaternion(),
                        scale: new Vector3()
                    };
                    matrix.decompose(trs.scale, trs.rotation, trs.position);
                    bindPose.push(trs);
                } else {
                    bindPose.push({
                        position: new Vector3(bindPoseArray[i * 10 + 0], bindPoseArray[i * 10 + 1], bindPoseArray[i * 10 + 2]),
                        rotation: new Quaternion(bindPoseArray[i * 10 + 3], bindPoseArray[i * 10 + 4], bindPoseArray[i * 10 + 5], bindPoseArray[i * 10 + 6]),
                        scale: new Vector3(bindPoseArray[i * 10 + 7], bindPoseArray[i * 10 + 8], bindPoseArray[i * 10 + 9])
                    });
                }
            }
            let rig = ctx.animationSet.rigs.map((ref)=>ref.get()).find((item)=>!!item && SkeletonRig.getRigKey(item.joints, item.rootJoint) === SkeletonRig.getRigKey(joints, rootJoint)) ?? null;
            if (!rig) {
                rig = new SkeletonRig(joints, bindPose, {
                    rootJoint,
                    rootBindPose
                });
                ctx.animationSet.rigs.push(new DRef(rig));
            }
            const skeleton = new SkinBinding(rig, inverseBindMatrices, joints);
            skeleton.persistentId = init.id;
            return {
                obj: skeleton,
                loadProps: false
            };
        },
        getInitParams (obj) {
            const inverseBindMatrices = obj.inverseBindMatrices.map((v)=>[
                    ...v
                ]).reduce((a, b)=>[
                    ...a,
                    ...b
                ], []);
            const bindPose = obj.bindPose.map((v)=>[
                    ...v.position,
                    ...v.rotation,
                    ...v.scale
                ]).reduce((a, b)=>[
                    ...a,
                    ...b
                ], []);
            return {
                joints: obj.joints.map((joint)=>joint.persistentId),
                rootJoint: obj.rig.rootJoint?.persistentId ?? '',
                rootBindPose: encodeSkeletonBindPose(obj.rig.rootBindPose),
                inverseBindMatrices: uint8ArrayToBase64(new Uint8Array(new Float32Array(inverseBindMatrices).buffer)),
                bindPose: uint8ArrayToBase64(new Uint8Array(new Float32Array(bindPose).buffer)),
                id: obj.persistentId
            };
        },
        getProps () {
            return [];
        }
    };
}
/** @internal */ function getSkeletonRigClass() {
    return {
        ctor: SkeletonRig,
        name: 'SkeletonRig',
        createFunc (ctx, init) {
            const prefabNode = ctx.getPrefabNode() ?? ctx;
            const joints = init.joints.map((id)=>prefabNode.findNodeById(id)).map((node)=>{
                node.jointTypeT = 'static';
                node.jointTypeS = 'static';
                node.jointTypeR = 'static';
                return node;
            });
            const rootJoint = init.rootJoint ? prefabNode.findNodeById(init.rootJoint) : null;
            const rootBindPose = init.rootBindPose ? decodeSkeletonBindPose(init.rootBindPose) : undefined;
            const bindPoseArray = new Float32Array(base64ToUint8Array(init.bindPose ?? init.bindPoseMatrices).buffer);
            const bindPose = [];
            for(let i = 0; i < joints.length; i++){
                if (!init.bindPose) {
                    const matrix = new Matrix4x4(bindPoseArray.slice(i * 16, i * 16 + 16));
                    const trs = {
                        position: new Vector3(),
                        rotation: new Quaternion(),
                        scale: new Vector3()
                    };
                    matrix.decompose(trs.scale, trs.rotation, trs.position);
                    bindPose.push(trs);
                } else {
                    bindPose.push({
                        position: new Vector3(bindPoseArray[i * 10 + 0], bindPoseArray[i * 10 + 1], bindPoseArray[i * 10 + 2]),
                        rotation: new Quaternion(bindPoseArray[i * 10 + 3], bindPoseArray[i * 10 + 4], bindPoseArray[i * 10 + 5], bindPoseArray[i * 10 + 6]),
                        scale: new Vector3(bindPoseArray[i * 10 + 7], bindPoseArray[i * 10 + 8], bindPoseArray[i * 10 + 9])
                    });
                }
            }
            const rig = new SkeletonRig(joints, bindPose, {
                rootJoint,
                rootBindPose
            });
            rig.persistentId = init.id;
            return {
                obj: rig,
                loadProps: false
            };
        },
        getInitParams (obj) {
            const bindPose = obj.bindPose.map((v)=>[
                    ...v.position,
                    ...v.rotation,
                    ...v.scale
                ]).reduce((a, b)=>[
                    ...a,
                    ...b
                ], []);
            return {
                joints: obj.joints.map((joint)=>joint.persistentId),
                rootJoint: obj.rootJoint?.persistentId ?? '',
                rootBindPose: encodeSkeletonBindPose(obj.rootBindPose),
                bindPose: uint8ArrayToBase64(new Uint8Array(new Float32Array(bindPose).buffer)),
                id: obj.persistentId
            };
        },
        getProps () {
            return [];
        }
    };
}
/** @internal */ function getSkinBindingClass() {
    return {
        ctor: SkinBinding,
        name: 'SkinBinding',
        createFunc (ctx, init) {
            const rig = ctx.findSkeletonRigById(init.rig);
            if (!rig) {
                throw new Error(`Deserialize SkinBinding failed: skeleton rig '${init.rig}' not found`);
            }
            const prefabNode = ctx.getPrefabNode() ?? ctx;
            const joints = (init.joints ?? rig.joints.map((joint)=>joint.persistentId)).map((id)=>prefabNode.findNodeById(id)).filter((node)=>!!node);
            const inverseBindMatricesArray = new Float32Array(base64ToUint8Array(init.inverseBindMatrices).buffer);
            const inverseBindMatrices = [];
            for(let i = 0; i < joints.length; i++){
                inverseBindMatrices.push(new Matrix4x4(inverseBindMatricesArray.slice(i * 16, i * 16 + 16)));
            }
            const binding = new SkinBinding(rig, inverseBindMatrices, joints);
            binding.persistentId = init.id;
            return {
                obj: binding,
                loadProps: false
            };
        },
        getInitParams (obj) {
            const inverseBindMatrices = obj.inverseBindMatrices.map((v)=>[
                    ...v
                ]).reduce((a, b)=>[
                    ...a,
                    ...b
                ], []);
            return {
                rig: obj.rig.persistentId,
                joints: obj.joints.map((joint)=>joint.persistentId),
                inverseBindMatrices: uint8ArrayToBase64(new Uint8Array(new Float32Array(inverseBindMatrices).buffer)),
                id: obj.persistentId
            };
        },
        getProps () {
            return [];
        }
    };
}
/** @internal */ function getAnimationClass(manager) {
    return {
        ctor: AnimationClip,
        name: 'AnimationClip',
        createFunc (ctx, init) {
            return {
                obj: ctx.animationSet.get(init) ?? ctx.animationSet.createAnimation(init, false)
            };
        },
        getInitParams (obj) {
            return obj.name;
        },
        getProps () {
            return defineProps([
                {
                    name: 'Name',
                    description: 'The animation name, unique in `AnimationSet`',
                    type: 'string',
                    get (value) {
                        value.str[0] = this.name;
                    }
                },
                {
                    name: 'Duration',
                    description: 'Time duration of the animation in seconds',
                    type: 'float',
                    default: 1,
                    get (value) {
                        value.num[0] = this.timeDuration;
                    },
                    set (value) {
                        this.timeDuration = value.num[0];
                    }
                },
                {
                    name: 'Weight',
                    description: 'Weight of this animation, used in animation blending',
                    type: 'float',
                    default: 1,
                    get (value) {
                        value.num[0] = this.weight;
                    },
                    set (value) {
                        this.weight = value.num[0];
                    }
                },
                {
                    name: 'AutoPlay',
                    description: 'Whether this animation should automatically start playing when model is loaded',
                    type: 'bool',
                    default: false,
                    get (value) {
                        value.bool[0] = this.autoPlay;
                    },
                    set (value) {
                        this.autoPlay = value.bool[0];
                    }
                },
                {
                    name: 'Rigs',
                    description: 'Rig references used by this animation clip',
                    type: 'object',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = [
                            ...this.skeletons
                        ];
                    },
                    set (value) {
                        if (!this.skeletons) {
                            this.skeletons = new Set();
                        }
                        for (const val of value.object[0] ?? []){
                            this.skeletons.add(val);
                        }
                    }
                },
                {
                    name: 'Skeletons',
                    description: 'Legacy skeleton references used by this animation clip',
                    type: 'object',
                    isHidden () {
                        return true;
                    },
                    get (value) {
                        value.object[0] = [];
                    },
                    set (value) {
                        if (!this.skeletons) {
                            this.skeletons = new Set();
                        }
                        for (const val of value.object[0] ?? []){
                            this.skeletons.add(val);
                        }
                    }
                },
                {
                    name: 'Tracks',
                    description: 'Animation tracks of this animation clip',
                    type: 'object_array',
                    options: {
                        edit: 'proptrack',
                        objectTypes: [
                            PropertyTrack
                        ]
                    },
                    readonly: true,
                    isHidden (index, obj) {
                        if (index < 0) {
                            return false;
                        } else {
                            return !(obj instanceof PropertyTrack);
                        }
                    },
                    get (value) {
                        value.object = [];
                        for (const tracks of this.tracks){
                            for (const track of tracks[1]){
                                if (tracks[0] instanceof SceneNode && !(track instanceof PropertyTrack)) {
                                    track.target = tracks[0].persistentId;
                                }
                            }
                            //value.object.push(...tracks[1].filter((track) => track instanceof PropertyTrack));
                            value.object.push(...tracks[1]);
                        }
                    },
                    set (value) {
                        for (const track of value.object){
                            if (track instanceof PropertyTrack) {
                                const targetObj = manager.findAnimationTarget(this._animationSet.model, track);
                                if (targetObj) {
                                    this.addTrack(targetObj, track);
                                }
                            } else if (track instanceof AnimationTrack) {
                                const prefabNode = this._animationSet.model.getPrefabNode() ?? this._animationSet.model;
                                const node = prefabNode.findNodeById(track.target);
                                if (node) {
                                    this.addTrack(node, track);
                                } else {
                                    console.error(`No node found with id = ${track.target}`);
                                }
                            }
                        }
                    },
                    delete (index) {
                        const trackList = [];
                        for (const tracks of this.tracks){
                            trackList.push(...tracks[1].filter((track)=>track instanceof PropertyTrack));
                        }
                        const trackToRemove = trackList[index];
                        if (trackToRemove) {
                            this.deleteTrack(trackToRemove);
                        }
                    }
                }
            ]);
        }
    };
}

export { getAnimationClass, getFixedGeometryCacheTrackClass, getInterpolatorClass, getJointDynamicsModifierClass, getJointDynamicsModifierSkeleton, getMorphTrackClass, getNodeEulerRotationTrackClass, getNodeRotationTrackClass, getNodeScaleTrackClass, getNodeTranslationTrackClass, getPCAGeometryCacheTrackClass, getPropTrackClass, getSkeletonClass, getSkeletonRigClass, getSkinBindingClass, setJointDynamicsModifierSkeleton };
//# sourceMappingURL=animation.js.map
